import { UI, HStack, Stack, VStack } from '@genial/components';
import { BoxRadius } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import React, { useEffect } from 'react';

import { InputMoney } from '../../../../../../../../../components/InputMoney';
import { OperationType } from '../../../../../../../../../components/OperationType';
import { useFundsSellAssetController } from '../../../../../hooks/products/FundsSell';
import { useFundsSellQuotationController } from '../../../../../hooks/products/FundsSell/quotation';
import { useFundsSellTaxSimulationController } from '../../../../../hooks/products/FundsSell/taxSimulation';
import { LateralBar } from '../../../components/LateralBar';

import { FundsItemSelectedTypes } from '.';

export const FundsItemSelectedMobile: React.FC<FundsItemSelectedTypes.Props> = ({
  offer,
  onUpdate,
  accountId,
  hideLateralBar = false,
}) => {
  const id = offer.productIdNormalized;
  const {
    control,
    handleInputBlur,
    handleInputClear,
    handleInputFocus,
    handleSubmit,
    hasError,
    helpText,
    isFocused,
    currentValueFormatted,
    currentValue,
    handleToggleIsTotalRedemption,
    isTotalRedemption,
    isTotalRedemptionChecked,
  } = useFundsSellAssetController(offer);

  const completeOffer = { ...offer, orderValue: currentValue, isTotalRedemption };

  const { isLoadingTaxes, taxSimulationResponse } = useFundsSellTaxSimulationController({
    valueToSimulate: currentValue,
    product: completeOffer,
  });

  const {
    requestDateErrorMessage,
    onChangeRequestDate,
    onResetRequestDate,
    selectedDate,
    quotationResponse,
    isLoadingDates,
  } = useFundsSellQuotationController({
    isTotalRedemption,
    product: offer,
    accountId,
  });

  useEffect(() => {
    if (offer.orderValue === currentValue || !currentValue) {
      return;
    }
    const updatedOffer = { ...offer, orderValue: currentValue || 0, isTotalRedemption };
    onUpdate(updatedOffer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValue]);

  useEffect(() => {
    if (offer.scheduleDate === selectedDate || !selectedDate || !quotationResponse) {
      return;
    }
    const updatedOffer = {
      ...offer,
      scheduleDate: selectedDate,
      dateQuotation: quotationResponse,
      isTotalRedemption,
    };
    onUpdate(updatedOffer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, quotationResponse]);

  const middleInfos = [
    {
      label: 'Tipo',
      value: <OperationType title="Resgate" />,
    },
    {
      label: 'Resgate Total',
      value: (
        <UI.Switch onToggle={handleToggleIsTotalRedemption} isChecked={isTotalRedemptionChecked} />
      ),
    },
    {
      label: 'IR Estimado',
      value: (
        <UI.Text color="neutral.dark" isLoading={isLoadingTaxes}>
          {taxSimulationResponse?.irValueNormalized || offer.estimatedIncomeTax}
        </UI.Text>
      ),
    },
    {
      label: 'Valor líq. estimado',
      value: (
        <UI.Text color="neutral.dark" isLoading={isLoadingTaxes}>
          {taxSimulationResponse?.liquidityValueNormalized || offer.estimatedNetValue}
        </UI.Text>
      ),
    },
    {
      label: 'Data de cotização',
      value: (
        <Stack minW="50px">
          <UI.Text color="neutral.dark" isLoading={isLoadingDates}>
            {quotationResponse?.quotationDateFormatted}
          </UI.Text>
        </Stack>
      ),
    },
    {
      label: 'Data de liquidação',
      value: (
        <Stack minW="50px">
          <UI.Text color="neutral.dark" isLoading={isLoadingDates}>
            {quotationResponse?.liquidityDateFormatted}
          </UI.Text>
        </Stack>
      ),
    },
  ];

  return (
    <VStack w="full">
      {!hideLateralBar && (
        <LateralBar
          backgroundColor="product.fundos"
          borderTopLeftRadius={BoxRadius.sm}
          borderBottomLeftRadius={BoxRadius.sm}
        />
      )}
      {offer.attentionMessage && (
        <Stack pb="stack.xxxs">
          <UI.Notice
            type="warning"
            backgroundColor={Utils.ColorToken.WithOpacity(
              'feedback.attention.lightest',
              'opacity.medium'
            )}
            textObjects={[
              { text: 'Atenção: ', variant: 'semibold' },
              { text: offer.attentionMessage, variant: 'regular' },
            ]}
          />
        </Stack>
      )}
      <VStack pb="stack.xxxs">
        <UI.Text variant="semibold" color="neutral.black">
          {offer.productName}
        </UI.Text>
      </VStack>
      <VStack space="stack.nano" pb="stack.xxxs" flexGrow={1}>
        {middleInfos.map(({ label, value }) => (
          <HStack space="inline.quarck" justifyContent="space-between" key={label}>
            <UI.Text color="neutral.black" variant="semibold">
              {label}
            </UI.Text>
            {value}
          </HStack>
        ))}
      </VStack>
      <VStack w="full" space="stack.quarck" pb="stack.xxxs" flexGrow={1} zIndex={10}>
        <UI.Text color="neutral.black" variant="semibold">
          Data de solicitação:
        </UI.Text>
        <Stack w="full">
          <UI.DatePicker
            date={selectedDate}
            onSelectDate={onChangeRequestDate}
            onClear={onResetRequestDate}
            availableRange={{ past: new Date(), onlyWeekdays: true }}
            error={requestDateErrorMessage}
            isDisabled={isLoadingDates}
          />
        </Stack>
      </VStack>
      <VStack w="full" space="stack.quarck" flexGrow={1}>
        <UI.Text color="neutral.black" variant="semibold">
          Valor aplicado:
        </UI.Text>
        <Stack w="full">
          <InputMoney
            id={`funds-sell-input${id}`}
            name="redemptionValue"
            control={control}
            handleFocus={handleInputFocus}
            handleBlur={handleInputBlur}
            handleSubmit={handleSubmit}
            handleClear={handleInputClear}
            currentValue={currentValueFormatted}
            hasFocus={isFocused}
            minValue={offer.minimumTransaction || 0}
            isDisabled={isTotalRedemptionChecked}
            customInputProps={{
              ...{ ...(isFocused && hasError && { error: hasError }) },
              ...{ ...(isFocused && !hasError && { helpText }) },
            }}
          />
        </Stack>
      </VStack>
    </VStack>
  );
};
